// src/components/oracle/OracleCardPull.tsx
// Oracle Card Reading Experience with beautiful animations and personalized interpretations

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RotateCcw, Bookmark, X, ChevronLeft } from 'lucide-react';
import { ORACLE_DECK, getRandomCard, getRandomCards, getSuitDisplayName } from '../../lib/mystara/oracle-deck';
import { useSoulFile } from '../../hooks/useSoulFile';
import { ToolHeader } from '../ui/ToolHeader';
import type { OracleCard, SpreadType, CardSuit } from '../../types/mystara';

interface OracleCardPullProps {
  onClose?: () => void;
  embedded?: boolean;
}

type PullState = 'select-spread' | 'focusing' | 'revealing' | 'reading';

const SPREAD_OPTIONS: { type: SpreadType; name: string; description: string; cardCount: number }[] = [
  { 
    type: 'single', 
    name: 'Daily Card', 
    description: 'A single card for guidance today',
    cardCount: 1
  },
  { 
    type: 'past-present-future', 
    name: 'Past · Present · Future', 
    description: 'Understand where you\'ve been, where you are, and where you\'re heading',
    cardCount: 3
  },
  { 
    type: 'identity', 
    name: 'Identity Spread', 
    description: 'Who you were · Who you are · Who you\'re becoming',
    cardCount: 3
  },
  { 
    type: 'block', 
    name: 'Block Spread', 
    description: 'The block · The truth · The path forward',
    cardCount: 3
  }
];

const SUIT_COLORS: Record<CardSuit, { bg: string; border: string; text: string }> = {
  identity: { bg: 'from-purple-100 to-pink-100', border: 'border-purple-200', text: 'text-purple-700' },
  integration: { bg: 'from-blue-100 to-cyan-100', border: 'border-blue-200', text: 'text-blue-700' },
  direction: { bg: 'from-amber-100 to-yellow-100', border: 'border-amber-200', text: 'text-amber-700' },
  shadow: { bg: 'from-slate-100 to-gray-100', border: 'border-slate-200', text: 'text-slate-700' }
};

export function OracleCardPull({ onClose, embedded = false }: OracleCardPullProps) {
  const { soulFile } = useSoulFile();
  const [pullState, setPullState] = useState<PullState>('select-spread');
  const [selectedSpread, setSelectedSpread] = useState<SpreadType | null>(null);
  const [drawnCards, setDrawnCards] = useState<OracleCard[]>([]);
  const [revealedIndex, setRevealedIndex] = useState(-1);
  const [interpretation, setInterpretation] = useState<string>('');
  const [question, setQuestion] = useState('');

  const handleSelectSpread = (spread: SpreadType) => {
    setSelectedSpread(spread);
    setPullState('focusing');
    
    // Brief focusing moment, then draw cards
    setTimeout(() => {
      const spreadConfig = SPREAD_OPTIONS.find(s => s.type === spread);
      const cards = getRandomCards(spreadConfig?.cardCount || 1);
      setDrawnCards(cards);
      setPullState('revealing');
    }, 2000);
  };

  const handleRevealCard = (index: number) => {
    if (index <= revealedIndex + 1) {
      setRevealedIndex(index);
      
      // If all cards revealed, generate interpretation
      if (index === drawnCards.length - 1) {
        setTimeout(() => {
          setPullState('reading');
          generateInterpretation();
        }, 1000);
      }
    }
  };

  const generateInterpretation = () => {
    // This would normally call the AI API for a personalized interpretation
    // For now, we'll create a template-based interpretation
    const cardInterpretations = drawnCards.map((card, i) => {
      const position = getPositionName(selectedSpread!, i);
      return `**${position}: ${card.name}**\n${card.lightInterpretation}\n\n*Reflection: ${card.reflectionQuestion}*`;
    });

    const intro = soulFile?.essence.preferredName 
      ? `${soulFile.essence.preferredName}, here's what the cards are showing you:\n\n`
      : "Here's what the cards are showing you:\n\n";

    setInterpretation(intro + cardInterpretations.join('\n\n'));
  };

  const handleReset = () => {
    setPullState('select-spread');
    setSelectedSpread(null);
    setDrawnCards([]);
    setRevealedIndex(-1);
    setInterpretation('');
    setQuestion('');
  };

  const handleClose = () => {
    if (pullState === 'select-spread' && onClose) {
      onClose();
    } else {
      handleReset();
    }
  };

  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto"
    : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40";

  return (
    <div className={wrapperClass}>
      <ToolHeader
        title="Self-Alignment Cards"
        onBack={pullState === 'select-spread' ? onClose : undefined}
        backLabel="Chat"
        rightAction={
          pullState !== 'select-spread' && (
            <button
              onClick={handleReset}
              className="p-2 text-purple-600 hover:bg-white/30 rounded-xl transition-colors"
              title="New reading"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          )
        }
      />

      <div className="max-w-2xl mx-auto px-6">
        <AnimatePresence mode="wait">
          {/* Spread Selection */}
          {pullState === 'select-spread' && (
            <motion.div
              key="select"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-purple-800 mb-2">
                  Choose Your Spread
                </h3>
                <p className="text-purple-500">
                  What guidance are you seeking?
                </p>
              </div>

              <div className="w-full max-w-lg mx-auto space-y-4">
                {SPREAD_OPTIONS.map((spread) => (
                  <motion.button
                    key={spread.type}
                    onClick={() => handleSelectSpread(spread.type)}
                    className="w-full p-5 rounded-2xl text-left bg-white/30 backdrop-blur-md border border-white/50 hover:bg-white/40 hover:border-purple-200/70 hover:shadow-lg hover:shadow-purple-500/10 transition-all group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-purple-800 group-hover:text-purple-900 transition-colors">
                          {spread.name}
                        </h4>
                        <p className="text-sm text-purple-600 mt-1">
                          {spread.description}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {Array.from({ length: spread.cardCount }).map((_, i) => (
                          <div
                            key={i}
                            className="w-6 h-8 rounded bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200/40"
                          />
                        ))}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Focusing State */}
          {pullState === 'focusing' && (
            <motion.div
              key="focusing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pt-32 text-center"
            >
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center"
              >
                <Sparkles className="w-10 h-10 text-purple-500" />
              </motion.div>
              <p className="text-xl text-purple-700">Take a breath...</p>
              <p className="text-purple-500 mt-2">Focus on what you need guidance with</p>
            </motion.div>
          )}

          {/* Card Reveal */}
          {pullState === 'revealing' && (
            <motion.div
              key="revealing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="pt-12"
            >
              <div className="text-center mb-8">
                <p className="text-purple-600">Tap each card to reveal</p>
              </div>

              <div className={`flex justify-center gap-4 ${drawnCards.length === 1 ? '' : 'flex-wrap'}`}>
                {drawnCards.map((card, index) => (
                  <motion.div
                    key={card.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    <CardDisplay
                      card={card}
                      isRevealed={index <= revealedIndex}
                      canReveal={index <= revealedIndex + 1}
                      onReveal={() => handleRevealCard(index)}
                      position={getPositionName(selectedSpread!, index)}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Reading/Interpretation */}
          {pullState === 'reading' && (
            <motion.div
              key="reading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="pt-8"
            >
              {/* Cards summary */}
              <div className="flex justify-center gap-3 mb-8">
                {drawnCards.map((card, index) => (
                  <div key={card.id} className="text-center">
                    <MiniCard card={card} />
                    <p className="text-xs text-purple-500 mt-2">
                      {getPositionName(selectedSpread!, index)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Interpretation */}
              <div className="bg-white/30 backdrop-blur-md rounded-2xl border border-white/50 p-6">
                <div className="max-w-none">
                  {interpretation.split('\n\n').map((paragraph, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.3 }}
                    >
                      {paragraph.startsWith('**') ? (
                        <h3 className="text-lg font-semibold text-purple-800 mt-6 first:mt-0">
                          {paragraph.replace(/\*\*/g, '')}
                        </h3>
                      ) : paragraph.startsWith('*') ? (
                        <p className="text-purple-600 italic text-sm mt-3">
                          {paragraph.replace(/\*/g, '')}
                        </p>
                      ) : (
                        <p className="text-purple-700 leading-relaxed">{paragraph}</p>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Reflection prompt */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="mt-8 pt-6 border-t border-white/30"
                >
                  <p className="text-purple-600 text-sm mb-3">
                    What does this bring up for you?
                  </p>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Write your reflections here..."
                    className="w-full p-4 bg-white/50 border border-white/50 rounded-xl text-purple-800 placeholder-purple-400 resize-none focus:outline-none focus:border-purple-400"
                    rows={3}
                  />
                </motion.div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleReset}
                  className="flex-1 py-3 bg-white/40 hover:bg-white/50 text-purple-700 border border-white/50 rounded-xl font-medium transition-colors"
                >
                  New Reading
                </button>
                <button className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity">
                  <Bookmark className="w-4 h-4" />
                  Save
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Card Display Component
function CardDisplay({ 
  card, 
  isRevealed, 
  canReveal, 
  onReveal, 
  position 
}: { 
  card: OracleCard;
  isRevealed: boolean;
  canReveal: boolean;
  onReveal: () => void;
  position: string;
}) {
  const colors = SUIT_COLORS[card.suit];

  return (
    <div className="relative">
      <p className="text-center text-xs text-purple-500 mb-2">{position}</p>

      <motion.button
        onClick={canReveal && !isRevealed ? onReveal : undefined}
        className={`relative w-32 h-48 rounded-xl overflow-hidden ${
          canReveal && !isRevealed ? 'cursor-pointer' : 'cursor-default'
        }`}
        whileHover={canReveal && !isRevealed ? { scale: 1.05 } : {}}
        whileTap={canReveal && !isRevealed ? { scale: 0.95 } : {}}
        animate={isRevealed ? { rotateY: 180 } : { rotateY: 0 }}
        transition={{ duration: 0.6 }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Card Back */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-purple-200/60 rounded-xl flex items-center justify-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-purple-500" />
          </div>
          {/* Decorative pattern */}
          <div className="absolute inset-4 border border-purple-300/30 rounded-lg" />
        </div>

        {/* Card Front */}
        <div 
          className={`absolute inset-0 bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-xl p-3 flex flex-col items-center justify-between`}
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <p className={`text-xs ${colors.text} opacity-70`}>
            {getSuitDisplayName(card.suit).replace(' Cards', '')}
          </p>
          
          <div className="text-center">
            <p className={`font-bold ${colors.text} text-sm leading-tight`}>
              {card.name}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-1">
            {card.keywords.slice(0, 2).map((keyword, i) => (
              <span key={i} className={`text-[10px] ${colors.text} opacity-60`}>
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </motion.button>
    </div>
  );
}

// Mini Card for summary view
function MiniCard({ card }: { card: OracleCard }) {
  const colors = SUIT_COLORS[card.suit];
  
  return (
    <div className={`w-16 h-24 rounded-lg bg-gradient-to-br ${colors.bg} border ${colors.border} p-2 flex flex-col items-center justify-center`}>
      <p className={`text-[10px] font-semibold ${colors.text} text-center leading-tight`}>
        {card.name}
      </p>
    </div>
  );
}

// Helper function
function getPositionName(spread: SpreadType, index: number): string {
  const positions: Record<SpreadType, string[]> = {
    single: ['Your Card'],
    'past-present-future': ['Past', 'Present', 'Future'],
    identity: ['Who You Were', 'Who You Are', 'Who You\'re Becoming'],
    block: ['The Block', 'The Truth', 'The Path Forward'],
    custom: ['Card ' + (index + 1)]
  };
  return positions[spread]?.[index] || `Card ${index + 1}`;
}

export default OracleCardPull;
